import { useEffect, useRef } from 'react'
import { signInWithCredential, GoogleAuthProvider } from 'firebase/auth'
import { auth } from '../config/firebase'

interface UseGoogleOneTapOptions {
  onSuccess?: (email: string, idToken: string) => void
  onError?: (error: string) => void
  disabled?: boolean
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            cancel_on_tap_outside?: boolean
            auto_select?: boolean
            itp_support?: boolean
            use_fedcm_for_prompt?: boolean
          }) => void
          prompt: (notificationCallback?: () => void) => void
          disableAutoSelect: () => void
        }
      }
    }
  }
}

export const useGoogleOneTap = ({
  onSuccess,
  onError,
  disabled = false
}: UseGoogleOneTapOptions = {}) => {
  const initializedRef = useRef(false)
  const scriptLoadedRef = useRef(false)
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)

  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
  }, [onSuccess, onError])

  useEffect(() => {
    if (disabled) return

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) {
      console.warn('Google One Tap: VITE_GOOGLE_CLIENT_ID not configured. Add it to your .env file.')
      console.warn('Get your Client ID from: https://console.cloud.google.com/apis/credentials')
      return
    }
    
    console.log('Google One Tap: Initializing with Client ID:', clientId.substring(0, 20) + '...')

    // Load Google Identity Services script
    const loadScript = () => {
      if (scriptLoadedRef.current) return

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => {
        scriptLoadedRef.current = true
        initializeOneTap()
      }
      script.onerror = () => {
        console.error('Failed to load Google Identity Services script')
      }
      document.head.appendChild(script)
    }

    // Initialize Google One Tap
    const initializeOneTap = () => {
      if (!window.google || initializedRef.current) return

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            try {
              // Exchange Google credential for Firebase credential
              const credential = GoogleAuthProvider.credential(response.credential)
              const userCredential = await signInWithCredential(auth, credential)
              
              // Get ID token
              const idToken = await userCredential.user.getIdToken()
              const email = userCredential.user.email || ''

              if (onSuccessRef.current) {
                onSuccessRef.current(email, idToken)
              }
            } catch (error: any) {
              console.error('Google One Tap sign-in error:', error)
              if (onErrorRef.current) {
                onErrorRef.current(error.message || 'Failed to sign in with Google')
              }
            }
          },
          cancel_on_tap_outside: true,
          auto_select: false,
          itp_support: true, // Support Intelligent Tracking Prevention
          use_fedcm_for_prompt: false, // Explicitly disable FedCM to avoid network errors
        })

        initializedRef.current = true

        // Show One Tap prompt after a short delay
        // This gives the page time to render
        // Success is handled via the initialize callback
        setTimeout(() => {
          if (window.google && initializedRef.current) {
            try {
              // Call prompt without notification callback
              // The callback in initialize() handles successful authentication
              window.google.accounts.id.prompt((notification) => {
                // Handle notification about prompt display
                if (notification.isNotDisplayed()) {
                  const reason = notification.getNotDisplayedReason()
                  // Only log non-user-dismissal reasons
                  if (reason !== 'user_cancel' && reason !== 'tap_outside') {
                    console.debug('Google One Tap not displayed:', reason)
                  }
                } else if (notification.isSkippedMoment()) {
                  const reason = notification.getSkippedReason()
                  console.debug('Google One Tap skipped:', reason)
                } else if (notification.isDismissedMoment()) {
                  const reason = notification.getDismissedReason()
                  // Only log non-user-dismissal reasons
                  if (reason !== 'user_cancel' && reason !== 'tap_outside') {
                    console.debug('Google One Tap dismissed:', reason)
                  }
                }
              })
            } catch (error) {
              // Silently handle expected errors (user dismissal, network issues, etc.)
              if (error instanceof Error) {
                // AbortError, NetworkError, and user cancellation are expected
                const expectedErrors = ['AbortError', 'NetworkError', 'aborted', 'cancelled', 'dismissed']
                const isExpected = expectedErrors.some(err => 
                  error.name === err || error.message?.toLowerCase().includes(err.toLowerCase())
                )
                if (!isExpected) {
                  console.warn('Google One Tap prompt error:', error)
                }
              }
            }
          }
        }, 1000)
      } catch (error) {
        console.error('Failed to initialize Google One Tap:', error)
      }
    }

    // Start loading script
    loadScript()

    // Cleanup
    return () => {
      if (window.google && initializedRef.current) {
        try {
          window.google.accounts.id.disableAutoSelect()
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    }
  }, [disabled])
}


